import _ from "lodash";
import { createSelector } from "reselect";

const propsInjector = (state, props) => props || {};

const getTourDetails = (state) => {
  return state.TourDetails?.details ?? {};
};
const tourIsRoundtripSelector = createSelector(
  [getTourDetails],
  (tourDetails) => {
    const isRoundtrip =
      (tourDetails?.destinations?.[0]?.name ?? "") ===
        _.last(tourDetails?.destinations ?? [])?.name ?? "";
    return isRoundtrip;
  }
);

const getStartDay = (order, details) => {
  const prevDests = (details?.destinations ?? []).filter(
    (pdest) => parseInt(pdest.order, 10) < order
  );

  const startDay = prevDests.reduce((a, b) => {
    return a + b.stay;
  }, 1);

  return startDay;
};
const setDays = (stay, startDay) => {
  const days = new Array(stay + 1).fill(1).map((i, idx) => startDay + idx);
  return days;
};
const updateDaysDataObject = (object, daysObject) => {
  object["days"].forEach((day) => {
    daysObject[day] = [..._.get(daysObject, day, []), object];
  });
};
const getDestDaysData = (details) => {
  const destDaysData = {};
  (details?.destinations ?? []).forEach((dest) => {
    const startDay = getStartDay(dest.order, details);
    dest["days"] = setDays(dest.stay, startDay);

    dest["events"] = {};
    dest.days.forEach((day, idx) => {
      if (idx === 0) {
        dest.events[day] = "arrival";
      } else if (idx + 1 < dest.days.length) {
        dest.events[day] = "stay";
      } else {
        dest.events[day] = "departure";
      }
    });

    updateDaysDataObject(dest, destDaysData);
  });

  return destDaysData;
};
const getAccDaysData = (details) => {
  const accDaysData = {};
  Object.entries(details?.accommodation_data ?? {}).forEach((entry) => {
    const acc = entry[1][0];
    acc["destOrder"] = parseInt(entry[0], 10);

    const dest = details.destinations.find(
      (dest) => parseInt(dest.order, 10) === parseInt(entry[0], 10)
    );
    const checkInDay = getStartDay(dest.order, details);
    acc["days"] = setDays(dest.stay, checkInDay);

    acc["events"] = {};
    acc.days.forEach((day, idx) => {
      if (idx === 0) {
        acc["events"][day] = "check_in";
      } else if (idx > 0 && idx + 1 < acc.days.length) {
        acc["events"][day] = "stay";
      } else {
        acc["events"][day] = "check_out";
      }
    });

    updateDaysDataObject(acc, accDaysData);
  });

  return accDaysData;
};
const getAddOnDaysData = (details) => {
  const data = {};
  Object.entries(details?.addon_data ?? {}).forEach((entry) => {
    data[entry[0]] = entry[1].map((ent) => ({
      ...ent,
      events: { [entry[0]]: "tour" },
    }));
  });

  return data;
};
const getFlightDaysData = (details, isRoundtrip) => {
  const flightDaysData = {};
  const flights = details?.transportation_data?.flights ?? {};

  Object.entries(flights).forEach((entry) => {
    const flight = entry[1][0];

    if (entry[0] === "extremalLeg") {
      flight["destOrder"] = 1;
      flight["destData"] = { name_en: details.destinations[0].name_en };

      flight["days"] = [1];
      flight["events"] = { 1: "arrival" };
      if (isRoundtrip) {
        const lastDay = _.last(Object.keys(details.content.itineraryDaysData));
        flight["days"].push(lastDay);
        flight["events"][lastDay] = "departure";
      }
    } else {
      const dest = details.destinations.find(
        (dest) => parseInt(dest.order, 10) === parseInt(entry[0], 10)
      );
      const startDay = getStartDay(dest.order, details);
      flight["destOrder"] = dest.order;
      flight["destData"] = { name_en: dest.name_en };
      flight["days"] = setDays(dest.stay, startDay);
      flight["days"] = [_.last(flight["days"])];
      flight["events"] = { [flight["days"][0]]: "departure" };
    }

    updateDaysDataObject(flight, flightDaysData);
  });

  return flightDaysData;
};
const getTransferDaysData = (details) => {
  const transferDaysData = {};
  const transfers = details?.transfer_data ?? {};

  Object.entries(transfers).forEach((entry) => {
    const trfs = entry[1]?.inbound_outbound;

    let inbound_trf = null;
    let outbound_trf = null;

    if (trfs) {
      inbound_trf = _.omit(trfs[0], "outbound");
      outbound_trf = _.omit(trfs[0], "inbound");
    } else {
      inbound_trf = trfs?.inbound?.[0] ?? {};
      outbound_trf = trfs?.outbound?.[0] ?? {};
    }

    inbound_trf["destOrder"] = entry[0];
    outbound_trf["destOrder"] = entry[0];

    const dest = details.destinations.find(
      (dest) => parseInt(dest.order, 10) === parseInt(entry[0], 10)
    );
    if (!dest) {
      return;
    }

    const startDay = getStartDay(dest.order, details);

    inbound_trf["destOrder"] = dest.order;
    outbound_trf["destOrder"] = dest.order;

    const days = setDays(dest.stay, startDay);
    inbound_trf["days"] = [days[0]];
    outbound_trf["days"] = [_.last(days)];

    inbound_trf["events"] = { [days[0]]: "inbound" };
    outbound_trf["events"] = { [_.last(days)]: "outbound" };
    updateDaysDataObject(inbound_trf, transferDaysData);
    updateDaysDataObject(outbound_trf, transferDaysData);
  });

  return transferDaysData;
};
export const dayByDayDataSelector = createSelector(
  [getTourDetails, tourIsRoundtripSelector],
  (details, isRoundtrip) => {
    if (details.product_type !== "PKG") {
      return null;
    }

    const day_by_day = _.get(details, "content.itineraryDaysData", {});

    const destDaysData = getDestDaysData(details);
    const accDaysData = getAccDaysData(details);
    const addOnDaysData = getAddOnDaysData(details);
    const flightsDaysData = getFlightDaysData(details, isRoundtrip);
    const transferDaysData = getTransferDaysData(details);

    Object.entries(day_by_day).forEach((entry) => {
      const dayIdx = parseInt(entry[0], 10);
      const dayData = entry[1];
      dayData["accommodations"] = accDaysData[dayIdx];
      dayData["destinations"] = destDaysData[dayIdx];
      dayData["addons"] = addOnDaysData[dayIdx];
      dayData["transfers"] = transferDaysData[dayIdx];
      dayData["flights"] = flightsDaysData[dayIdx];
    });

    return day_by_day;
  }
);

const getPkgMetadata = (state) => state.WebsiteMetadata?.packagesMetadata ?? {};
const getCountriesInfo = (state) => state.CountriesInfo;
export const topCountriesMetaSelector = createSelector(
  [getPkgMetadata, getCountriesInfo],
  (pkgs, countries) => {
    const country_codes = [
      ...new Set(
        _.flattenDeep(
          (pkgs?.destinations ?? []).map((dest) => dest.country_code)
        )
      ),
    ];

    const info = {};
    Object.entries(countries)
      .filter((entry) => country_codes.includes(entry[0]))
      .forEach((entry) => (info[entry[0]] = entry[1]));

    return { country_codes, info };
  }
);

const getToursAndOrdering = (state) => {
  const { list, ordering, product_type } = state.ToursList;
  return { list, ordering, product_type };
};
export const getToursListSelector = createSelector(
  [getToursAndOrdering],
  (tours_and_order) => {
    const { list, ordering, product_type } = tours_and_order;

    if (product_type === "PKG") {
      return list;
    } else {
      return list.sort((a, b) => {
        const pricea = a.min_price_from.value;
        const priceb = b.min_price_from.value;

        if (ordering === "price_low_to_high") {
          return pricea - priceb;
        } else if (ordering === "price_high_to_low") {
          return priceb - pricea;
        } else {
          return 0;
        }
      });
    }
  }
);

export const getToursGroupedListSelector = createSelector(
  [getToursListSelector],
  (tours) => {
    const groups = _.groupBy(tours, (tour) => tour?.destinations?.[0]?.name_en);
    return groups;
  }
);

export const tripServicesListSelector = createSelector(
  [getTourDetails, tourIsRoundtripSelector, propsInjector],
  (tourDetails, isRoundtrip, props = { noDoubleSrvs: false }) => {
    const { noDoubleSrvs } = props;

    var services = [];

    const flights = tourDetails?.transportation_data?.flights ?? {};

    const originFlight = flights?.extremalLeg?.[0];
    services.push({ service: originFlight, event: "departure", srvType: "FL" });

    const transfers = tourDetails.transfer_data;
    tourDetails.destinations.forEach((dest) => {
      const { order: dest_order, name_en } = dest;
      const destObj = { name_en, dest_order };
      const destTransfer = transfers?.[dest_order]?.inbound_outbound?.[0];
      let inboundTransfer, outboundTransfer;
      const trfMeta = { srvType: "TRF", destination: { ...destObj } };
      if (destTransfer) {
        inboundTransfer = {
          service: destTransfer,
          event: "inbound",
          ...trfMeta,
        };
        outboundTransfer = {
          service: destTransfer,
          event: "outbound",
          ...trfMeta,
        };
      } else {
        inboundTransfer = _.get(transfers, `${dest_order}.inbound.0`);
        outboundTransfer = _.get(transfers, `${dest_order}.outbound.0`);
        if (inboundTransfer) {
          inboundTransfer = {
            service: inboundTransfer,
            event: "inbound",
            ...trfMeta,
          };
        }
        if (outboundTransfer) {
          outboundTransfer = {
            service: outboundTransfer,
            event: "outbound",
            ...trfMeta,
          };
        }
      }

      let accommodation = _.get(
        tourDetails.accommodation_data,
        `${dest_order}.0`
      );

      if (accommodation) {
        accommodation = {
          service: accommodation,
          event: "check_in",
          srvType: "ACC",
          destination: { ...destObj },
        };
      }

      let addons = _.flatten(
        Object.values(_.get(tourDetails, "addon_data", {}))
      ).filter((addon) => {
        return (
          _.get(addon, "destOrder", 0).toString() === dest_order.toString()
        );
      });
      addons = addons.map((addon) => ({
        service: addon,
        srvType: "MI",
        event: "tour",
        destination: { ...destObj },
      }));

      let departingFlight = _.get(flights, `${dest_order}.0`);
      if (departingFlight) {
        departingFlight = {
          service: departingFlight,
          event: "departure",
          srvType: "FL",
        };
      }

      if (inboundTransfer) {
        services.push(inboundTransfer);
      }
      if (accommodation) {
        services.push(accommodation);
      }
      if (addons.length) {
        services = [...services, ...addons];
      }
      if (outboundTransfer) {
        services.push(outboundTransfer);
      }
      if (departingFlight) {
        services.push(departingFlight);
      }
    });

    if (isRoundtrip && !noDoubleSrvs) {
      const returnFlight = _.get(flights, "extremalLeg.0");
      services.push({ service: returnFlight, event: "arrival", srvType: "FL" });
    }

    return services;
  }
);

export const pkgTransfersRequiredSelector = createSelector(
  [getTourDetails],
  (details) =>
    _.get(details, "itinerary_setup.requiredServices", []).includes("TF")
);

const getProductCategories = (state) => {
  const {
    packages,
    addons,
    flights,
    accommodation,
    transfers,
    restaurants,
    activities,
    ferries,
    trains,
    car_rental,
    quotations,
  } = state.WebsiteMetadata?.metadata ?? {};

  return {
    packages,
    addons,
    flights,
    accommodation,
    transfers,
    restaurants,
    activities,
    ferries,
    trains,
    car_rental,
    quotations,
  };
};
export const getProductCategoriesSelector = createSelector(
  [getProductCategories],
  (categories) => {
    const product_types = [];
    if (categories.packages) {
      product_types.push("PKG");
    }

    if (categories.addons) {
      product_types.push("MI");
    }

    if (categories.accommodation) {
      product_types.push("ACC");
    }

    if (categories.transfers) {
      product_types.push("TRF");
    }

    if (categories.flights) {
      product_types.push("FL");
    }

    if (categories.restaurants) {
      product_types.push("REST");
    }

    if (categories.activities) {
      product_types.push("ACT");
    }

    if (categories.ferries) {
      product_types.push("FER");
    }

    if (categories.trains) {
      product_types.push("TRA");
    }

    if (categories.car_rental) {
      product_types.push("CAR");
    }

    if (categories.quotations) {
      product_types.push("QUO");
    }

    return product_types;
  }
);

const getWebsiteMeta = (state) => state.WebsiteMetadata;
export const getWebsiteCurrencySelector = createSelector(
  [getWebsiteMeta],
  (meta) => {
    return _.get(meta, "metadata.currency");
  }
);

export * from "./pkg_price_search_selectors";
